import { useEffect, useState } from "react";
import { 
  Card, 
  CardContent,
  CardHeader,
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PokemonFilters, fetchPokemonTypes, countMatchingPokemon } from "@/services/pokeApi";
import { useLanguage } from "@/services/languageService";

interface PokemonFilterProps {
  onGenerate: (count: number, filters: PokemonFilters) => void;
}

const PokemonFilter = ({ onGenerate }: PokemonFilterProps) => {
  const { t } = useLanguage();
  const [count, setCount] = useState<number>(4);
  const [type, setType] = useState<string>("");
  const [generation, setGeneration] = useState<string>("");
  const [rarity, setRarity] = useState<string>("");
  const [evolutionStage, setEvolutionStage] = useState<string>("all");
  const [types, setTypes] = useState<{ name: string, url: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [matchingCount, setMatchingCount] = useState<number | null>(null);
  const [isCountLoading, setIsCountLoading] = useState(false);

  useEffect(() => {
    // Load Pokemon types on component mount
    const loadTypes = async () => {
      const typeData = await fetchPokemonTypes();
      // Filter out any "stella" type if it exists
      const filteredTypes = typeData.filter(type => type.name !== "stella");
      setTypes(filteredTypes);
    };
    
    loadTypes();
  }, []);

  // Effect to count matching Pokémon when filters change
  useEffect(() => {
    const getMatchingCount = async () => {
      const filters: PokemonFilters = {};
      
      if (type && type !== "any") filters.type = type;
      if (generation && generation !== "any") filters.generation = generation;
      if (rarity && rarity !== "any") filters.rarity = rarity;
      if (evolutionStage && evolutionStage !== "all") {
        filters.evolutionStage = evolutionStage as PokemonFilters['evolutionStage'];
      }
      
      const activeFilterKeys = Object.keys(filters).filter(key => {
        if (key === 'evolutionStage') return filters.evolutionStage !== 'all';
        return filters[key as keyof Omit<PokemonFilters, 'evolutionStage'>] !== "" && filters[key as keyof Omit<PokemonFilters, 'evolutionStage'>] !== "any";
      });

      if (activeFilterKeys.length > 0) {
        setIsCountLoading(true);
        try {
          const count = await countMatchingPokemon(filters);
          setMatchingCount(count);
        } catch (error) {
          console.error("Error counting Pokémon:", error);
          setMatchingCount(null);
        } finally {
          setIsCountLoading(false);
        }
      } else {
        setMatchingCount(null);
      }
    };
    
    getMatchingCount();
  }, [type, generation, rarity, evolutionStage]);

  const handleGenerate = () => {
    setIsLoading(true);
    const filters: PokemonFilters = {};
    
    if (type && type !== "any") filters.type = type;
    if (generation && generation !== "any") filters.generation = generation;
    if (rarity && rarity !== "any") filters.rarity = rarity;
    if (evolutionStage && evolutionStage !== "all") {
        filters.evolutionStage = evolutionStage as PokemonFilters['evolutionStage'];
    }
    
    onGenerate(count, filters);
    setTimeout(() => setIsLoading(false), 1000); // Ensure button shows loading state
  };

  const generations = [
    { value: "generation-i", label: "Generation I" },
    { value: "generation-ii", label: "Generation II" },
    { value: "generation-iii", label: "Generation III" },
    { value: "generation-iv", label: "Generation IV" },
    { value: "generation-v", label: "Generation V" },
    { value: "generation-vi", label: "Generation VI" },
    { value: "generation-vii", label: "Generation VII" },
    { value: "generation-viii", label: "Generation VIII" },
    { value: "generation-ix", label: "Generation IX" },
  ];

  const evolutionStages = [
    { value: "all", label: "evolutionStageAll" },
    { value: "fully-evolved", label: "evolutionStageFullyEvolved" },
    { value: "not-fully-evolved", label: "evolutionStageNotFullyEvolved" },
  ];

  return (
    <Card className="w-full shadow-lg">
      <CardHeader className="bg-gradient-to-r from-pokemon-red to-pokemon-blue pb-6">
        <CardTitle className="text-2xl text-center text-white">{t("title")}</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <Label htmlFor="count" className="block mb-2">
              {t("numberOfPokemon")}: {count}
            </Label>
            <Slider
              id="count"
              min={1}
              max={8}
              step={1}
              value={[count]}
              onValueChange={(values) => setCount(values[0])}
              className="mb-6"
            />
          </div>
          
          <div>
            <Label htmlFor="type" className="block mb-2">
              {t("type")}
            </Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="type">
                <SelectValue placeholder={t("anyType")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("anyType")}</SelectItem>
                {types.map((pokemonType) => (
                  <SelectItem key={pokemonType.name} value={pokemonType.name}>
                    {t(pokemonType.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="generation" className="block mb-2">
              {t("generation")}
            </Label>
            <Select value={generation} onValueChange={setGeneration}>
              <SelectTrigger id="generation">
                <SelectValue placeholder={t("anyGeneration")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("anyGeneration")}</SelectItem>
                {generations.map((gen) => (
                  <SelectItem key={gen.value} value={gen.value}>
                    {t(gen.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="rarity" className="block mb-2">
              {t("minimumRarity")}
            </Label>
            <Select value={rarity} onValueChange={setRarity}>
              <SelectTrigger id="rarity">
                <SelectValue placeholder={t("anyRarity")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">{t("anyRarity")}</SelectItem>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                  <SelectItem key={value} value={value.toString()}>
                    {value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="evolutionStage" className="block mb-2">
              {t("evolutionStage")}
            </Label>
            <Select value={evolutionStage} onValueChange={setEvolutionStage}>
              <SelectTrigger id="evolutionStage">
                <SelectValue placeholder={t("evolutionStageAll")} />
              </SelectTrigger>
              <SelectContent>
                {evolutionStages.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {t(stage.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Matching Pokemon Count */}
        {(isCountLoading || matchingCount !== null) && (
          <div className="mt-4 text-center">
            {isCountLoading ? (
              <p>{t("countingPokemon")}...</p>
            ) : (
              <p className="text-sm font-medium">
                {t("matchingPokemonCount", { count: matchingCount || 0 })}
              </p>
            )}
          </div>
        )}
        
        <div className="flex justify-center mt-6">
          <Button 
            onClick={handleGenerate} 
            className="bg-pokemon-red hover:bg-red-600 text-white px-6 py-2 rounded-full font-semibold shadow-lg transform transition hover:scale-105"
            disabled={isLoading || (matchingCount !== null && matchingCount === 0)}
          >
            {isLoading ? t("generating") : t("generateButton")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PokemonFilter;
